import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Register Page — 2026 Stripe × Linear Standard
 * 
 * Design Principles:
 * - Slightly more spacing than login
 * - Emphasize "Start in minutes"
 * - Progressive disclosure (fewer fields initially)
 * - Password strength indicator
 * - Calm, trustworthy onboarding
 */
export default function Register2026() {
  const { register } = useAuth();
  const navigate = useNavigate();
  
  // Form state
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    licenseKey: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  
  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  
  // Refs
  const nameRef = useRef(null);
  
  // Autofocus name on mount
  useEffect(() => {
    nameRef.current?.focus();
  }, []);
  
  // Calculate password strength
  useEffect(() => {
    const password = formData.password;
    if (!password) {
      setPasswordStrength(0);
      return;
    }
    
    let strength = 0;
    
    // Length
    if (password.length >= 8) strength += 25;
    if (password.length >= 12) strength += 25;
    
    // Complexity
    if (/[a-z]/.test(password)) strength += 15;
    if (/[A-Z]/.test(password)) strength += 15;
    if (/[0-9]/.test(password)) strength += 10;
    if (/[^a-zA-Z0-9]/.test(password)) strength += 10;
    
    setPasswordStrength(Math.min(strength, 100));
  }, [formData.password]);
  
  // Validation functions
  const validateName = (value) => {
    if (!value || value.trim() === '') {
      return 'Name is required';
    }
    if (value.trim().length < 2) {
      return 'Name must be at least 2 characters';
    }
    return null;
  };
  
  const validateEmail = (value) => {
    if (!value || value.trim() === '') {
      return 'Email is required';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      return 'Enter a valid email address';
    }
    return null;
  };
  
  const validatePassword = (value) => {
    if (!value || value.trim() === '') {
      return 'Password is required';
    }
    if (value.length < 8) {
      return 'Password must be at least 8 characters';
    }
    return null;
  };
  
  const validateLicenseKey = (value) => {
    if (!value || value.trim() === '') {
      return 'License key is required';
    }
    // Format: XXXX-XXXX-XXXX-XXXX (UUID v4 format with dashes)
    const licenseKeyRegex = /^[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}$/i;
    if (!licenseKeyRegex.test(value.trim())) {
      return 'License key must be in format XXXX-XXXX-XXXX-XXXX';
    }
    return null;
  };
  
  // Handle field blur (progressive validation)
  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    
    let error = null;
    if (field === 'fullName') {
      error = validateName(formData.fullName);
    } else if (field === 'email') {
      error = validateEmail(formData.email);
    } else if (field === 'password') {
      error = validatePassword(formData.password);
    } else if (field === 'licenseKey') {
      error = validateLicenseKey(formData.licenseKey);
    }
    
    setErrors(prev => ({ ...prev, [field]: error }));
  };
  
  // Handle field change
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };
  
  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    
    // Validate all fields
    const nameError = validateName(formData.fullName);
    const emailError = validateEmail(formData.email);
    const passwordError = validatePassword(formData.password);
    const licenseKeyError = validateLicenseKey(formData.licenseKey);
    
    const newErrors = {
      fullName: nameError,
      email: emailError,
      password: passwordError,
      licenseKey: licenseKeyError,
    };
    
    setErrors(newErrors);
    setTouched({ fullName: true, email: true, password: true, licenseKey: true });
    
    // Stop if validation fails
    if (nameError || emailError || passwordError || licenseKeyError) {
      return;
    }
    
    // Proceed with registration
    setIsLoading(true);
    
    try {
      const result = await register(
        formData.fullName, 
        formData.email, 
        formData.password,
        formData.licenseKey.trim().toUpperCase()
      );
      setFormData({ ...formData, password: '', licenseKey: '' });
      // Navigate to login after successful registration
      navigate('/login');
    } catch (err) {
      // Extract clean error message
      let errorMessage = err.message || 'Failed to create account';
      
      // Remove IPC error prefix if present
      if (errorMessage.includes('Error invoking remote method')) {
        const match = errorMessage.match(/Error: (.+)$/);
        if (match) {
          errorMessage = match[1];
        }
      }
      
      // Show error at top of form
      setErrors(prev => ({
        ...prev,
        submit: errorMessage,
      }));
    } finally {
      setIsLoading(false);
    }
  };
  
  // Password strength color
  const getStrengthColor = () => {
    if (passwordStrength < 40) return '#DC2626'; // Red
    if (passwordStrength < 70) return '#F59E0B'; // Amber
    return '#10B981'; // Green
  };
  
  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Escape clears errors
      if (e.key === 'Escape') {
        setErrors({});
        setSubmitAttempted(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  
  return (
    <div 
      className="min-h-screen flex items-center justify-center py-12"
      style={{
        background: '#FAFBFC',
      }}
    >
      {/* Auth Container - Dense, minimal */}
      <div className="w-full max-w-[400px] px-6">
        {/* Auth Form Container */}
        <div 
          className="relative bg-white rounded-[12px] px-10 py-6 animate-fadeIn"
          style={{
            boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08), 0 8px 24px rgba(15, 23, 42, 0.04)',
          }}
        >
          {/* Header - Informational, not marketing */}
          <div className="mb-6">
            <h1 
              className="text-[21px] font-semibold text-slate-900 mb-1"
              style={{ 
                fontFamily: 'Inter, system-ui, sans-serif',
                letterSpacing: '-0.012em',
              }}
            >
              Create your account
            </h1>
            <p className="text-[13px] text-slate-500">
              Start invoicing in minutes
            </p>
          </div>
          
          {/* Submit Error - Red, visible */}
          {submitAttempted && errors.submit && (
            <div 
              className="mb-4 px-3.5 py-2.5 rounded-lg bg-red-50"
              style={{
                border: '1px solid rgba(220, 38, 38, 0.2)',
              }}
              role="alert"
            >
              <p className="text-[13px] font-medium text-red-700">
                {errors.submit}
              </p>
            </div>
          )}
          
          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-3.5">
            {/* Full Name Field - Dense, quiet */}
            <div>
              <label 
                htmlFor="fullName"
                className="block text-[12px] font-medium text-slate-700 mb-1.5"
              >
                Full name
              </label>
              <input
                ref={nameRef}
                id="fullName"
                name="fullName"
                type="text"
                autoComplete="name"
                value={formData.fullName}
                onChange={(e) => handleChange('fullName', e.target.value)}
                onBlur={() => handleBlur('fullName')}
                disabled={isLoading}
                placeholder="John Doe"
                className={`
                  w-full h-[40px] px-3 text-[14px]
                  bg-white
                  rounded-[8px]
                  transition-all duration-100
                  placeholder:text-slate-300
                  disabled:opacity-50 disabled:cursor-not-allowed
                  focus:outline-none
                  ${touched.fullName && errors.fullName
                    ? 'border border-slate-200 focus:border-red-400'
                    : 'border border-slate-200 focus:border-[rgba(99,102,241,0.6)]'
                  }
                `}
                style={{
                  fontFamily: 'Inter, system-ui, sans-serif',
                }}
              />
              {touched.fullName && errors.fullName && (
                <p className="mt-1.5 text-[12px] text-red-600">
                  Please enter your full name
                </p>
              )}
            </div>
            
            {/* Email Field - Dense, quiet */}
            <div>
              <label 
                htmlFor="email"
                className="block text-[12px] font-medium text-slate-700 mb-1.5"
              >
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                onBlur={() => handleBlur('email')}
                disabled={isLoading}
                placeholder="you@example.com"
                className={`
                  w-full h-[40px] px-3 text-[14px]
                  bg-white
                  rounded-[8px]
                  transition-all duration-100
                  placeholder:text-slate-300
                  disabled:opacity-50 disabled:cursor-not-allowed
                  focus:outline-none
                  ${touched.email && errors.email
                    ? 'border border-slate-200 focus:border-red-400'
                    : 'border border-slate-200 focus:border-[rgba(99,102,241,0.6)]'
                  }
                `}
                style={{
                  fontFamily: 'Inter, system-ui, sans-serif',
                }}
              />
              {touched.email && errors.email && (
                <p className="mt-1.5 text-[12px] text-red-600">
                  That email doesn't look right
                </p>
              )}
            </div>
            
            {/* License Key Field - Dense, quiet */}
            <div>
              <label 
                htmlFor="licenseKey"
                className="block text-[12px] font-medium text-slate-700 mb-1.5"
              >
                License key
              </label>
              <input
                id="licenseKey"
                name="licenseKey"
                type="text"
                autoComplete="off"
                value={formData.licenseKey}
                onChange={(e) => {
                  // Auto-format license key as user types
                  let value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                  if (value.length > 16) value = value.slice(0, 16);
                  
                  // Add dashes automatically
                  if (value.length > 12) {
                    value = value.slice(0, 4) + '-' + value.slice(4, 8) + '-' + value.slice(8, 12) + '-' + value.slice(12);
                  } else if (value.length > 8) {
                    value = value.slice(0, 4) + '-' + value.slice(4, 8) + '-' + value.slice(8);
                  } else if (value.length > 4) {
                    value = value.slice(0, 4) + '-' + value.slice(4);
                  }
                  
                  handleChange('licenseKey', value);
                }}
                onBlur={() => handleBlur('licenseKey')}
                disabled={isLoading}
                placeholder="XXXX-XXXX-XXXX-XXXX"
                className={`
                  w-full h-[40px] px-3 text-[14px] font-mono
                  bg-white
                  rounded-[8px]
                  transition-all duration-100
                  placeholder:text-slate-300
                  disabled:opacity-50 disabled:cursor-not-allowed
                  focus:outline-none
                  ${touched.licenseKey && errors.licenseKey
                    ? 'border border-slate-200 focus:border-red-400'
                    : 'border border-slate-200 focus:border-[rgba(99,102,241,0.6)]'
                  }
                `}
              />
              {touched.licenseKey && errors.licenseKey && (
                <p className="mt-1.5 text-[12px] text-red-600">
                  {errors.licenseKey}
                </p>
              )}
              {!errors.licenseKey && formData.licenseKey && (
                <p className="mt-1.5 text-[12px] text-slate-500">
                  Enter the license key you received
                </p>
              )}
            </div>
            
            {/* Password Field - Dense, quiet */}
            <div>
              <label 
                htmlFor="password"
                className="block text-[12px] font-medium text-slate-700 mb-1.5"
              >
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={formData.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  onBlur={() => handleBlur('password')}
                  disabled={isLoading}
                  placeholder="At least 8 characters"
                  className={`
                    w-full h-[40px] px-3 pr-10 text-[14px]
                    bg-white
                    rounded-[8px]
                    transition-all duration-100
                    placeholder:text-slate-300
                    disabled:opacity-50 disabled:cursor-not-allowed
                    focus:outline-none
                    ${touched.password && errors.password
                      ? 'border border-slate-200 focus:border-red-400'
                      : 'border border-slate-200 focus:border-[rgba(99,102,241,0.6)]'
                    }
                  `}
                  style={{
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-50"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              
              {/* Password Strength Indicator - Dots only, minimal */}
              {formData.password && (
                <div className="mt-1.5">
                  <div className="flex gap-1">
                    {[0, 1, 2, 3].map((index) => (
                      <div
                        key={index}
                        className="h-0.5 flex-1 rounded-full bg-slate-200 transition-all duration-100"
                        style={{
                          backgroundColor: passwordStrength > index * 25 ? getStrengthColor() : undefined,
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}
              
              {touched.password && errors.password && (
                <p className="mt-1.5 text-[12px] text-red-600">
                  Use at least 8 characters
                </p>
              )}
            </div>
            
            {/* Submit Button - Inevitable, not flashy */}
            <button
              type="submit"
              disabled={isLoading}
              className="
                w-full h-[42px] mt-4
                bg-[#6366F1] hover:bg-[#5558E3]
                text-white font-medium text-[14px]
                rounded-[10px]
                transition-all duration-100
                disabled:opacity-50 disabled:cursor-not-allowed
                focus:outline-none focus:ring-2 focus:ring-[rgba(99,102,241,0.2)] focus:ring-offset-1
                relative overflow-hidden
              "
              style={{
                fontFamily: 'Inter, system-ui, sans-serif',
              }}
            >
              {isLoading && (
                <span className="absolute inset-0 flex items-center justify-center">
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </span>
              )}
              <span className={isLoading ? 'opacity-0' : ''}>
                Continue
              </span>
            </button>
          </form>
          
          {/* Trust Microcopy - Minimal */}
          <div className="mt-4 text-center">
            <p className="text-[11px] text-slate-400">
              Offline-first. Your data stays on your device.
            </p>
          </div>
          
          {/* Switch to Login - Continuous, no divider */}
          <div className="mt-6 text-center">
            <p className="text-[13px] text-slate-600">
              Already have an account?{' '}
              <Link
                to="/login"
                className="text-slate-900 font-medium hover:text-[#6366F1] transition-colors"
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
      
      {/* Animations - Minimal */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-4px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 150ms ease-out;
        }
      `}</style>
    </div>
  );
}
